import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/AuthService";
import TokenService from "../services/TokenService";
import { GetControleAcessoDto } from "../shared/dtos/ControleAcesso.dto";
import { ModulosEnum } from "../shared/enum/modulos.enum";
import { PermissoesEnum } from "../shared/enum/permicao.enum";

export interface User {
  id: number;
  name: string;
  email: string;
  permission: string;
  isActive: boolean;
  controleAcesso: GetControleAcessoDto[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoggingOut: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  checkPermission: (modulo: ModulosEnum, permisson: PermissoesEnum) => boolean;
  logout: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(TokenService.getToken());
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Efeito para carregar o estado inicial
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = TokenService.getToken();
        const storedUser = sessionStorage.getItem("user");

        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);

          if (
            TokenService.validateToken(storedToken) &&
            parsedUser &&
            parsedUser.email &&
            parsedUser.permission
          ) {
            setToken(storedToken);
            setUser(parsedUser);
            setIsAuthenticated(true);
          } else {
            handleInvalidSession();
          }
        } else {
          handleInvalidSession();
        }
      } catch (error) {
        console.error("Erro ao inicializar autenticação:", error);
        handleInvalidSession();
      }
    };

    initializeAuth();
  }, []);

  // Efeito para verificar a validade do token periodicamente
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenInterval = setInterval(async () => {
      try {
        const currentToken = TokenService.getToken();
        if (!currentToken || TokenService.isTokenExpired()) {
          const newToken = await TokenService.refreshTokenIfNeeded();
          if (!newToken) {
            handleInvalidSession();
          }
        }
      } catch (error) {
        console.error("Erro ao verificar token:", error);
        handleInvalidSession();
      }
    }, 60000); // Verifica a cada minuto

    return () => clearInterval(checkTokenInterval);
  }, [isAuthenticated]);

  const handleInvalidSession = () => {
    // TokenService.clearToken();
    sessionStorage.removeItem("user");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      const { accessToken, user: userData } = response;

      if (accessToken && userData) {
        setToken(accessToken);
        setUser(userData);
        setIsAuthenticated(true);
        navigate("/list-contribuinte", { replace: true });
      } else {
        throw new Error("Dados de autenticação incompletos");
      }
    } catch (error) {
      console.error("Erro no login:", error);
      handleInvalidSession();
      throw error;
    }
  };

  const logout = () => {
    setIsLoggingOut(true);
    authService.logout();
    handleInvalidSession();
    navigate("/", { replace: true });
    setTimeout(() => {
      setIsLoggingOut(false);
    }, 100);
  };

  const checkPermission = (modulo: ModulosEnum, permisson: PermissoesEnum) => {
    if (!user) {
      return false;
    }

    const hasEditPermission = !!user?.controleAcesso?.some(
      (controle) =>
        controle.modulos?.nome === modulo &&
        controle.permissoes?.role === permisson
    );

    return hasEditPermission;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoggingOut,
        isAuthenticated,
        login,
        logout,
        checkPermission,
        setUser,
        setToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
